import { useDispatch, useSelector } from 'react-redux'
import { setEnteringInputDialog } from '../../VNDialogs/ActionCreators'
import { setTaxExemptData } from '../../actions/cart'
import useRemoteOrderTotal from '../../hooks/useRemoteOrderTotal'
import useSpinner from '../../context/hooks/useSpinner'
import { isTabbed } from '../../utils/orderStates'
import { getOrderInProgress } from '../../selectors/order'
import { getTabsModeEnabled } from '../../selectors/menus'
import { useEffect, useState } from 'react'
import {
  exemptTaxOnOrder,
  exemptTaxOnOrderFailed,
  exemptTaxOnOrderSucceeded,
  orderDetailsRequested,
  setOrderInProgressWithId,
  updateOrderTotals,
} from '../../actions/order'
import { calculateServiceCharge, calculateSubtotal } from '../../utils/totalUtils'
import { getCartItemsFromOrder, getTax } from '../../selectors/cart'
import { getMenuServiceCharge } from '../../selectors/items'
import { retotalOrder } from '../../VNReopenTab/Apis'
import { ToastManager } from '../../utils/toastManager'
import { centsToDollars } from '../../utils/formatters'
import useTaxResults from './useTaxResults'
import { getManagerId } from '../../selectors/attendant'

const useTaxes = () => {
  const dispatch = useDispatch()
  const [loadRemoteOrderTotal] = useRemoteOrderTotal()
  const { startSpinner, stopSpinner } = useSpinner()

  const [shouldRetotal, setShouldRetotal] = useState(false)

  const orderInProgress = useSelector(getOrderInProgress)
  const tabbed = isTabbed(orderInProgress)
  const tabsModeEnabled = useSelector(getTabsModeEnabled)
  const lineItems = useSelector((state) => getCartItemsFromOrder(state, orderInProgress.uuid))
  const serviceCharge = useSelector(getMenuServiceCharge)
  const tax = useSelector((state) => getTax(state, true)) //skip tax exemptions
  const { taxAmountInCents } = useTaxResults()

  useEffect(() => {
    if (shouldRetotal) {
      startSpinner()
      loadRemoteOrderTotal(true, undefined, undefined, true).finally(() => {
        ToastManager.success(`Success. ${centsToDollars(taxAmountInCents)} tax exempted.`)
        stopSpinner()
        setShouldRetotal(false)
      })
    }
    //We need to remove loadRemoteOrderTotal from the dependency array since we can't wrap it in useCallback without causing other issues.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldRetotal, startSpinner, stopSpinner, taxAmountInCents])

  const closeDialog = () => {
    dispatch(setEnteringInputDialog({ open: false, title: '', onSaveText: '', onCancelText: '', onSave: () => {} }))
  }

  const exemptOrder = (reason, taxExemptApproverUuid) => {
    dispatch(setTaxExemptData({ reason, taxExempt: true, taxExemptApproverUuid }))

    //on tabs menu, pay now orders need to be retotaled
    if (tabsModeEnabled && !tabbed) {
      setShouldRetotal(true)
    } else {
      //if no retotal needed then just show toast
      ToastManager.success(`Success. ${centsToDollars(tax)} tax exempted.`)
    }
  }

  const exemptTabbedOrder = (reason, taxExemptApproverUuid) => {
    if (orderInProgress.wasCreatedInStadium) {
      exemptTabbedOrderOnline(reason, taxExemptApproverUuid)
      return
    }
    exemptTabbedOrderOffline(reason, taxExemptApproverUuid)
  }

  const exemptTabbedOrderOnline = async (reason, taxExemptApproverUuid) => {
    const orderUuid = orderInProgress.uuid

    startSpinner()

    try {
      dispatch(exemptTaxOnOrder({ orderUuid, taxExemptReason: reason, taxExemptApproverUuid }))

      const promotions = orderInProgress?.promotions || []
      await retotalOrder(orderUuid, promotions, true, reason, taxExemptApproverUuid)

      //get updated values from stadium second param being true means that the order in progress will be refreshed
      dispatch(orderDetailsRequested(orderUuid, true))

      dispatch(exemptTaxOnOrderSucceeded())

      ToastManager.success(`Success. ${centsToDollars(taxAmountInCents)} tax exempted.`)
    } catch (_error) {
      dispatch(exemptTaxOnOrderFailed(orderUuid))
      stopSpinner()
      ToastManager.error(`There was an error exempting the order`)
    }
  }

  const exemptTabbedOrderOffline = (reason, taxExemptApproverUuid) => {
    dispatch(exemptTaxOnOrder({ orderUuid: orderInProgress.uuid, taxExemptReason: reason, taxExemptApproverUuid }))
    retotalOfflineOrder()
    dispatch(exemptTaxOnOrderSucceeded())
    ToastManager.success(`Success. ${centsToDollars(taxAmountInCents)} tax exempted.`)
  }

  const retotalOfflineOrder = () => {
    const orderId = orderInProgress.uuid
    const subtotalAmountInCents = calculateSubtotal(lineItems)

    const serviceFee = {
      name: serviceCharge.name,
      total: calculateServiceCharge(serviceCharge, subtotalAmountInCents) ?? 0,
      legal: serviceCharge.legal,
      taxClassUuid: serviceCharge?.taxClassUuid,
    }

    const taxRulesBreakdown = {}
    const amountInCents = subtotalAmountInCents + serviceFee.total
    const balanceDueInCents = amountInCents

    //store the current breakdown as exemptable tax rules this will let us calculate what the previous tax amount was
    const exemptableTaxRules = orderInProgress.taxRulesBreakdown || {}

    dispatch(
      updateOrderTotals({
        orderId,
        subtotalAmountInCents,
        taxAmountInCents: 0,
        amountInCents,
        balanceDueInCents,
        serviceFee,
        taxRulesBreakdown,
        exemptableTaxRules,
      }),
    )

    //refresh order in progress with updated order
    dispatch(setOrderInProgressWithId(orderId))
  }

  const exemptTax = () => {
    dispatch((_, getState) => {
      const taxExemptApproverUuid = getManagerId(getState())
      dispatch(
        setEnteringInputDialog({
          open: true,
          title: 'Exempt Tax Reason',
          onSaveText: 'DONE',
          onCancelText: 'CANCEL',
          onSave: (reason) => {
            closeDialog()

            if (tabbed) {
              exemptTabbedOrder(reason, taxExemptApproverUuid)
              return
            }

            exemptOrder(reason, taxExemptApproverUuid)
          },
        }),
      )
    })
  }

  return { exemptTax }
}

export default useTaxes
